import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { deliveriesApi, type Delivery } from "../api/deliveries";
import { supplierFinanceApi, type SupplierFinanceDetail } from "../api/supplierFinance";
import { procurementContractsApi, type ProcurementContract } from "../api/procurementContracts";
import { card, head, list, itemLink, moreLink, empty } from "./widgetStyles";

/** Koliko se sopstvenih aktivnih ugovora čita za ovu tablu; preko toga tabla kaže da je odsečeno. */
const UGOVORA_NA_TABLI = 100;

/**
 * What a co-operative wants on opening the portal: where its loads are, how its
 * contracts stand, what it is owed and when it was last paid. Chosen by
 * `supplier_finance:view_own`.
 *
 * <p>Documents are reached through the contract or the delivery they belong to;
 * there is no aggregate document feed and this widget does not invent one.
 */
export function SupplierSnapshot({ limit = 4 }: { limit?: number }) {
  const { t, i18n } = useTranslation();
  const [deliveries, setDeliveries] = useState<Delivery[] | null>(null);
  const [finance, setFinance] = useState<SupplierFinanceDetail | null>(null);
  const [contracts, setContracts] = useState<ProcurementContract[] | null>(null);
  const [contractsCut, setContractsCut] = useState(false);

  useEffect(() => {
    // Odsecanje radi server. Dok je ovde stajalo `.slice(0, limit)`, ceo registar isporuka je putovao
    // do pregledača da bi se prikazalo njih nekoliko.
    void deliveriesApi.list({}, 0, limit).then((r) => setDeliveries(r.content)).catch(() => setDeliveries([]));
    void supplierFinanceApi.me().then(setFinance).catch(() => setFinance(null));
    // Svoji ugovori, po strani: kooperant ih u berbi ima nekoliko, ali broj iznad table ne sme da
    // bude sabran nad delom registra bez reči o tome.
    void procurementContractsApi
      .list({ status: "ACTIVE" }, 0, UGOVORA_NA_TABLI)
      .then((strana) => {
        setContracts(strana.content);
        setContractsCut(strana.totalElements > strana.content.length);
      })
      .catch(() => setContracts([]));
  }, [limit]);

  if (deliveries === null) return null;

  const money = (v: number, currency: string) =>
    `${v.toLocaleString(i18n.resolvedLanguage, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

  return (
    <section style={card}>
      <header style={head}>
        <strong>{t("widgets.supplierSnapshot.title", "My deliveries")}</strong>
        <Link to="/procurement/deliveries" className="widget-more-link" style={moreLink}>
          {t("widgets.supplierSnapshot.viewAll", "All deliveries")}
        </Link>
      </header>

      {contracts && contracts.length > 0 && (
        <p style={{ margin: 0, fontSize: 13, color: "var(--ink)" }}>
          {t("widgets.supplierSnapshot.contracts", "My contracts")}:{" "}
          <strong>
            {contracts
              // Contracts in force only: the server knows the fulfilment of each of them.
              .reduce((sum, c) => sum + (c.settledT ?? 0), 0)
              .toLocaleString(i18n.resolvedLanguage, { maximumFractionDigits: 2 })}{" "}
            t
          </strong>
          <span style={{ color: "var(--ink-3)" }}>
            {" / "}
            {contracts
              .reduce((sum, c) => sum + c.quantityT, 0)
              .toLocaleString(i18n.resolvedLanguage, { maximumFractionDigits: 2 })}{" "}
            t
          </span>
          {contractsCut && (
            <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
              {" · "}
              {t("widgets.supplierSnapshot.contractsCut", "nad prvih {{n}} ugovora", {
                n: UGOVORA_NA_TABLI,
              })}
            </span>
          )}
        </p>
      )}

      {finance && (
        <p style={{ margin: 0, fontSize: 13, color: "var(--ink)" }}>
          {t("widgets.supplierSnapshot.balance", "Bankom owes")}:{" "}
          <strong>{money(finance.account.balanceTotal, finance.account.currency)}</strong>
        </p>
      )}

      {finance && finance.payments.length > 0 && (
        <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
          {t("widgets.supplierSnapshot.lastPayment", "Last payment")}:{" "}
          {new Date(finance.payments[0].paymentDate).toLocaleDateString(i18n.resolvedLanguage)} ·{" "}
          {money(finance.payments[0].amount, finance.payments[0].currency)}
        </p>
      )}

      {deliveries.length === 0 ? (
        <p style={empty}>{t("widgets.supplierSnapshot.empty", "No announced deliveries.")}</p>
      ) : (
        <ul style={list}>
          {deliveries.map((d) => (
            <li key={d.id}>
              <Link to={`/procurement/deliveries/${d.id}`} style={itemLink}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <small style={{ color: "var(--ink-3)" }}>
                    {/* Uvezen prijem nema najavu, pa se pokazuje dan kad je roba primljena. Bez ovoga
                        bi na praznom datumu stajalo „Invalid Date". */}
                    {d.receivedOn ?? d.announcedDate
                      ? new Date((d.receivedOn ?? d.announcedDate) as string).toLocaleDateString(i18n.resolvedLanguage)
                      : "—"}
                  </small>
                  <small style={{ color: "var(--brand-ink)" }}>
                    {t(`deliveries.status.${d.status.toLowerCase()}`, d.status)}
                  </small>
                </div>
                <small style={{ display: "block", color: "var(--ink-3)", marginTop: 2 }}>
                  {d.netKg != null
                    ? `${(d.netKg / 1000).toFixed(2)} t`
                    : d.estimatedQuantityT != null
                      ? `${d.estimatedQuantityT} t`
                      : "—"}{" "}· {d.vehiclePlate ?? "—"}
                </small>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
